import React, { useEffect, useState } from 'react';
import { ModalShell } from '../ds/ModalShell';
import { InputField } from '../ds/InputField';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';
import { termsApi } from '../services/api';
import type { ScheduleImportItem, ScheduleImportResponse } from '../services/api';
import { formatDate } from '../utils/dateFormat';
import { budynkiLabel, lokaleLabel } from '../utils/plural';

export interface ScheduleImportModalProps {
  open: boolean;
  onClose: () => void;
  /** Zatwierdzenie coś zapisało — rodzic odświeża listę budynków. */
  onDone: () => void;
}

const ACTION_LABEL: Record<ScheduleImportItem['action'], string> = {
  plan: 'Zaplanuje',
  already: 'Bez zmian',
  conflict: 'Konflikt',
  skip: 'Pominięty',
};

const ACTION_COLOR: Record<ScheduleImportItem['action'], string> = {
  plan: 'var(--status-ok)',
  already: 'var(--text-secondary)',
  conflict: 'var(--status-rec)',
  skip: 'var(--status-deny)',
};

const banner = (kind: 'error' | 'ok' | 'warn'): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-label)',
  background: kind === 'error' ? 'var(--status-deny-bg)' : kind === 'ok' ? 'var(--status-ok-bg)' : 'var(--status-rec-bg)',
  border: `var(--border-width) solid ${kind === 'error' ? 'var(--status-deny-bar)' : kind === 'ok' ? 'var(--status-ok-bar)' : 'var(--status-rec-bar)'}`,
  color: kind === 'error' ? 'var(--status-deny)' : kind === 'ok' ? 'var(--status-ok)' : 'var(--status-rec)',
});

const th: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  background: 'var(--surface-fill)',
  textAlign: 'left',
  padding: 'var(--space-2) var(--space-3)',
  borderBottom: 'var(--border-width) solid var(--border-strong)',
  color: 'var(--text-secondary)',
  fontWeight: 'var(--weight-semibold)' as any,
  whiteSpace: 'nowrap',
};

const td: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  borderBottom: 'var(--border-width) solid var(--border)',
  color: 'var(--text-primary)',
  verticalAlign: 'top',
};

const day = (iso: string | null) => (iso ? formatDate(iso) : '—');

/** Kolumna „Powód / uwagi": powód, uwaga z harmonogramu, lokale, dopasowanie po skrócie, wynik zapisu. */
function notesOf(it: ScheduleImportItem, committed: boolean): string[] {
  const out: string[] = [];
  if (it.reason && !(it.action === 'skip' && it.note && it.reason.includes(it.note))) out.push(it.reason);
  if (it.apartmentsAdd.length > 0) {
    out.push(`${committed ? 'dodano' : 'doda'} ${lokaleLabel(it.apartmentsAdd.length)} (${it.apartmentsAdd[0]}–${it.apartmentsAdd[it.apartmentsAdd.length - 1]})`);
  } else if (it.apartmentsNote && (it.action === 'plan' || it.action === 'already')) {
    out.push(`lokale: ${it.apartmentsNote}`);
  }
  if (it.matchHow === 'words') out.push('dopasowano po skrócie ulicy');
  if (it.result?.error) out.push(`BŁĄD: ${it.result.error}`);
  return out;
}

/**
 * A11 (uwagi Bronka, raport 2026-10-19): „Importuj harmonogram (Excel)" z panelu. Kroki: wybór pliku → podgląd
 * (serwer czyta plik i mówi, co zrobi z KAŻDYM wierszem; nic nie zapisuje) → „Zaplanuj N budynków" (zapis tylko
 * tych budynków, które podgląd pokazał jako „do zapisu" — zmiana w bazie w międzyczasie kończy się konfliktem
 * albo pominięciem z powodem, nigdy nadpisaniem terminów). Logika po stronie serwera: services/scheduleImport.js
 * (ta sama co skrypt CLI). Wiersz z tekstem zamiast daty („Czekamy na remont dachu") jest pomijany, a tekst widać
 * w kolumnie „Uwaga z harmonogramu".
 */
export default function ScheduleImportModal({ open, onClose, onDone }: ScheduleImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [city, setCity] = useState('Zabrze');
  const [addApartments, setAddApartments] = useState(false);
  const [preview, setPreview] = useState<ScheduleImportResponse | null>(null);
  const [result, setResult] = useState<ScheduleImportResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState(0);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setCity('Zabrze');
    setAddApartments(false);
    setPreview(null);
    setResult(null);
    setError(null);
    setInputKey((k) => k + 1);
  }, [open]);

  // Zmiana pliku albo opcji unieważnia podgląd — zatwierdzić można tylko to, co się widziało.
  const invalidate = () => { setPreview(null); setError(null); };

  const runPreview = async (opts: { addApartments?: boolean } = {}) => {
    if (!file || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await termsApi.importXlsx({
        file, mode: 'preview', city: city.trim() || 'Zabrze', addApartments: opts.addApartments ?? addApartments,
      });
      setPreview(res);
    } catch (e) {
      setPreview(null);
      setError(e instanceof Error && e.message ? e.message : 'Nie udało się wczytać harmonogramu');
    } finally {
      setBusy(false);
    }
  };

  const toWrite = preview ? preview.items.filter((it) => it.writes && it.building) : [];
  const toPlan = toWrite.filter((it) => it.action === 'plan').length;

  const commit = async () => {
    if (!file || !preview || toWrite.length === 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await termsApi.importXlsx({
        file,
        mode: 'commit',
        city: preview.city,
        addApartments: preview.addApartments,
        buildingIds: toWrite.map((it) => it.building!.id),
      });
      setResult(res);
      onDone();
    } catch (e) {
      // Odpowiedź mogła zginąć po zapisie — lista ma pokazać stan z serwera.
      onDone();
      setError(e instanceof Error && e.message ? e.message : 'Nie udało się zapisać harmonogramu');
    } finally {
      setBusy(false);
    }
  };

  const shown = result || preview;
  const committed = !!result;
  const commitLabel = toPlan > 0
    ? `Zaplanuj ${budynkiLabel(toPlan)}`
    : `Dodaj lokale (${budynkiLabel(toWrite.length)})`;

  const footer = result ? (
    <Button variant="primary" onClick={onClose}>Zamknij</Button>
  ) : (
    <>
      <Button variant="secondary" onClick={onClose} disabled={busy}>Anuluj</Button>
      {!preview ? (
        <Button variant="primary" icon="upload" data-testid="schedule-import-preview" disabled={!file || busy} onClick={() => { void runPreview(); }}>
          {busy ? 'Wczytywanie…' : 'Pokaż podgląd'}
        </Button>
      ) : (
        <Button variant="primary" icon="calendar" data-testid="schedule-import-commit" disabled={toWrite.length === 0 || busy} onClick={() => { void commit(); }}>
          {busy ? 'Zapisywanie…' : (toWrite.length === 0 ? 'Nic do zaplanowania' : commitLabel)}
        </Button>
      )}
    </>
  );

  return (
    <ModalShell
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      title={result ? 'Wynik importu harmonogramu' : 'Importuj harmonogram (Excel)'}
      width="min(96vw, 1100px)"
      footer={footer}
    >
      <div data-testid="schedule-import" style={{ display: 'grid', gap: 'var(--space-4)', fontFamily: 'var(--font-sans)' }}>
        {!result && (
          <>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
              Plik .xlsx z kolumnami <strong>Adres</strong>, <strong>Nr bud</strong>, <strong>Ilość mieszkań</strong>,
              {' '}<strong>Data I kontroli</strong> i <strong>Data popołudniu</strong> (= II termin). Najpierw podgląd — nic nie
              zostanie zapisane, dopóki nie klikniesz „Zaplanuj”. Planowane są tylko budynki „Nowy” bez otwartego przeglądu;
              istniejących terminów import nigdy nie zmienia.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-4)', alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <label htmlFor="schedule-import-file" style={{ fontSize: 'var(--text-label)', fontWeight: 'var(--weight-semibold)', color: 'var(--text-secondary)' }}>
                  Plik harmonogramu (.xlsx)
                </label>
                <input
                  key={inputKey}
                  id="schedule-import-file"
                  data-testid="schedule-import-file"
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  disabled={busy}
                  onChange={(e) => { setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null); invalidate(); }}
                  style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-label)' }}
                />
              </div>
              <InputField
                label="Miejscowość (Excel nie ma tej kolumny)"
                value={city}
                disabled={busy}
                onChange={(e) => { setCity(e.target.value); invalidate(); }}
                containerStyle={{ maxWidth: 260 }}
              />
            </div>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)', cursor: 'pointer', fontSize: 'var(--text-label)' }}>
              <input
                type="checkbox"
                data-testid="schedule-import-apartments"
                checked={addApartments}
                disabled={busy}
                onChange={(e) => {
                  const next = e.target.checked;
                  setAddApartments(next);
                  // Podgląd od razu z nową opcją — liczba lokali do dodania musi się zgadzać z tym, co się zatwierdza.
                  if (preview) void runPreview({ addApartments: next });
                }}
              />
              <span>
                Utwórz lokale 1..N z kolumny „Ilość mieszkań” — tylko w budynkach, które nie mają jeszcze żadnego lokalu
                <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>
                  Budynków z lokalami import nie zmienia (numeracja z CEEB bywa nieciągła).
                </span>
              </span>
            </label>
          </>
        )}

        {error && (
          <div role="alert" style={banner('error')}>
            <Icon name="triangle-alert" size={18} />
            <span>{error}</span>
          </div>
        )}

        {shown && (
          <>
            <div data-testid="schedule-import-summary" style={committed ? banner('ok') : banner('warn')}>
              <Icon name={committed ? 'check-circle' : 'clipboard'} size={18} />
              <span>
                {committed && shown.result
                  ? `Zaplanowano ${budynkiLabel(shown.result.planned)}, dodano ${lokaleLabel(shown.result.apartmentsAdded)}${shown.result.errors ? `, błędy: ${shown.result.errors}` : ''}.`
                  : `Arkusz „${shown.file.sheet}”, ${shown.summary.rows} wierszy: zaplanuje ${shown.summary.plan}, bez zmian ${shown.summary.already}, `
                    + `konflikty ${shown.summary.conflict}, pominięte ${shown.summary.skip}`
                    + (shown.addApartments ? `; lokale do utworzenia: ${shown.summary.apartmentsToAdd} w ${budynkiLabel(shown.summary.buildingsWithApartments)}` : '')
                    + '.'}
              </span>
            </div>
            <div style={{ overflow: 'auto', maxHeight: '52vh', border: 'var(--border-width) solid var(--border)', borderRadius: 'var(--radius-md)' }}>
              <table aria-label="Wiersze harmonogramu" style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'var(--text-label)' }}>
                <thead>
                  <tr>
                    <th style={th}>Wiersz</th>
                    <th style={th}>Adres (Excel)</th>
                    <th style={th}>Budynek w bazie</th>
                    <th style={th}>I termin</th>
                    <th style={th}>II termin</th>
                    <th style={th}>Lokale baza / Excel</th>
                    <th style={th}>Wynik</th>
                    <th style={th}>Uwaga z harmonogramu</th>
                    <th style={th}>Powód / uwagi</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.items.map((it) => (
                    <tr key={it.rowNumber} data-testid={`schedule-import-row-${it.rowNumber}`}>
                      <td style={td}>{it.rowNumber}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{it.excelAddress || '—'}</td>
                      <td style={td}>{it.building ? it.building.label : '—'}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{day(it.firstTermDate)}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{day(it.secondTermDate)}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{`${it.apartmentsDb ?? '—'} / ${it.apartmentCountExcel ?? '—'}`}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 'var(--weight-semibold)' as any, color: ACTION_COLOR[it.action] }}>
                        {committed && it.writes ? '✓ ' : ''}{ACTION_LABEL[it.action]}
                      </td>
                      <td style={{ ...td, color: 'var(--status-rec)' }}>{it.note || ''}</td>
                      <td style={{ ...td, color: 'var(--text-secondary)' }}>{notesOf(it, committed).join('; ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!committed && toWrite.length > 0 && (
              <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
                „{commitLabel}” zapisze {toPlan > 0 ? `przegląd (I i II termin z pliku) w ${budynkiLabel(toPlan)}` : 'tylko lokale'}
                {preview && preview.addApartments && preview.summary.apartmentsToAdd > 0 ? ` i utworzy ${lokaleLabel(preview.summary.apartmentsToAdd)}` : ''}.
                Przegląd z terminem od razu trafia na tablety (jak przy „Zaplanuj”).
              </p>
            )}
          </>
        )}
      </div>
    </ModalShell>
  );
}
